import { useNavigation, useDelete } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Button, Space, Popconfirm, Switch, Tag, message, Tooltip, Alert, Typography, Steps } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';

const { Text } = Typography;

interface AppUpdate {
  id: number;
  version: string | null;
  androidVersion: string | null;
  iosVersion: string | null;
  isMandatory: boolean;
  playStoreUrl: string;
  appStoreUrl: string;
  releaseNotesUz: string;
  releaseNotesRu: string;
  releaseNotesEn: string;
  isActive: boolean;
  createdAt: string;
}

export const AppUpdateList = () => {
  const { edit, create } = useNavigation();
  const { mutate: deleteUpdate } = useDelete();
  const { can } = usePermission();

  const { tableProps, tableQueryResult } = useTable<AppUpdate>({
    resource: 'admin/app-updates',
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
  });

  const handleToggle = async (id: number, currentValue: boolean) => {
    try {
      await apiClient.put(`/admin/app-updates/${id}`, { isActive: !currentValue });
      message.success(currentValue ? "Yangilanish o'chirildi" : 'Yangilanish faollashtirildi');
      tableQueryResult.refetch();
    } catch {
      message.error(t('common.error'));
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: t('appUpdates.version'),
      key: 'version',
      width: 200,
      render: (_: unknown, record: AppUpdate) => (
        <Space size={4}>
          <Tag color="green">🤖 {record.androidVersion ?? record.version ?? '—'}</Tag>
          <Tag color="blue"> {record.iosVersion ?? record.version ?? '—'}</Tag>
        </Space>
      ),
    },
    {
      title: t('common.type'),
      dataIndex: 'isMandatory',
      key: 'isMandatory',
      width: 120,
      render: (mandatory: boolean) => (
        <Tag color={mandatory ? 'red' : 'green'}>
          {mandatory ? t('appUpdates.mandatory') : t('appUpdates.optional')}
        </Tag>
      ),
    },
    {
      title: t('appUpdates.releaseNotesUz'),
      dataIndex: 'releaseNotesUz',
      key: 'releaseNotesUz',
      ellipsis: true,
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date: string) =>
        new Date(date).toLocaleDateString('uz-UZ', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      title: t('common.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (active: boolean, record: AppUpdate) => (
        <Switch
          checked={active}
          onChange={() => handleToggle(record.id, active)}
          checkedChildren={t('common.active')}
          unCheckedChildren={t('common.inactive')}
          size="small"
        />
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      render: (_: unknown, record: AppUpdate) => (
        <Space>
          {can('settings', 'update') && (
            <Tooltip title="Tahrirlash">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => edit('admin/app-updates', record.id)}
              />
            </Tooltip>
          )}
          {can('settings', 'delete') && (
            <Popconfirm
              title="O'chirishni tasdiqlaysizmi?"
              onConfirm={() =>
                deleteUpdate({
                  resource: 'admin/app-updates',
                  id: record.id,
                })
              }
            >
              <Tooltip title="O'chirish">
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <List
      title={t('appUpdates.title')}
      headerButtons={
        can('settings', 'create') ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => create('admin/app-updates')}
          >
            Yangi versiya
          </Button>
        ) : null
      }
    >
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
        message="Yangi versiya chiqarish tartibi"
        description={
          <div>
            <Steps
              direction="vertical"
              size="small"
              current={-1}
              style={{ marginTop: 12 }}
              items={[
                {
                  title: <Text strong>1. Ilovani build qiling</Text>,
                  description: (
                    <Text type="secondary">
                      Flutter loyihada versiyani <Text code>pubspec.yaml</Text> da yangilang (masalan: <Text code>version: 1.2.0+2</Text>).
                      Keyin <Text code>flutter build appbundle</Text> (Android) va <Text code>flutter build ios</Text> (iOS) buyruqlarini bajaring.
                    </Text>
                  ),
                },
                {
                  title: <Text strong>2. Do'konlarga yuklang</Text>,
                  description: (
                    <Text type="secondary">
                      Google Play Console va App Store Connect ga yangi versiyani yuklang. Review jarayonini boshlang va tasdiqlashni kuting.
                    </Text>
                  ),
                },
                {
                  title: <Text strong>3. Do'konlarda tasdiqlangandan keyin</Text>,
                  description: (
                    <Text type="secondary">
                      Do'konlarda yangilanish <strong>tasdiqlangach</strong>, shu yerda "Yangi versiya" tugmasini bosing.
                      <strong> Android</strong> va <strong>iOS</strong> versiyalarini alohida kiriting — App Store review sekinroq bo'lgani uchun ular farq qilishi mumkin.
                      Har bir do'kon uchun o'sha do'kondagi <strong>eng so'nggi</strong> versiyani yozing.
                    </Text>
                  ),
                },
                {
                  title: <Text strong>4. Yangilanish turini tanlang</Text>,
                  description: (
                    <Text type="secondary">
                      <strong>Majburiy</strong> — foydalanuvchi yangilanmasdan ilovaga kira olmaydi (katta o'zgarishlar uchun).
                      {' '}<strong>Ixtiyoriy</strong> — foydalanuvchi "Keyinroq" tugmasini bosib davom etishi mumkin.
                    </Text>
                  ),
                },
              ]}
            />
          </div>
        }
      />

      <Table {...tableProps} columns={columns} rowKey="id" />
    </List>
  );
};
